import { useEffect, useState, useMemo } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import {
  CheckCircle,
  ArrowRight,
  Sparkles,
  Package,
  Pencil,
  CalendarDays,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import Navbar from "@/components/Navbar";
import confetti from "canvas-confetti";
import {
  ADD_ONS,
  CORE_PRICE_CENTS,
  formatPrice,
  type PersonalizationTag,
} from "@/lib/personalization";

const fireConfetti = () => {
  const duration = 2500;
  const end = Date.now() + duration;

  const frame = () => {
    confetti({
      particleCount: 3,
      angle: 60,
      spread: 55,
      origin: { x: 0, y: 0.7 },
      colors: ["#4A8C72", "#C9A96E", "#2D5A3D", "#E8DCC8"],
    });
    confetti({
      particleCount: 3,
      angle: 120,
      spread: 55,
      origin: { x: 1, y: 0.7 },
      colors: ["#4A8C72", "#C9A96E", "#2D5A3D", "#E8DCC8"],
    });
    if (Date.now() < end) requestAnimationFrame(frame);
  };

  confetti({
    particleCount: 80,
    spread: 100,
    origin: { y: 0.6 },
    colors: ["#4A8C72", "#C9A96E", "#2D5A3D", "#E8DCC8", "#F5F0E8"],
  });
  frame();
};

const PaymentSuccess = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [countdown, setCountdown] = useState(10);
  const [show, setShow] = useState(false);

  const selectedTags = useMemo(() => {
    const raw = searchParams.get("tags") || "";
    return raw
      .split(",")
      .map((t) => t.trim().toUpperCase())
      .filter((t) =>
        ["METABOLIC", "ENERGY", "RECOVERY", "HORMONAL"].includes(t)
      ) as PersonalizationTag[];
  }, [searchParams]);

  const selectedAddons = useMemo(
    () => ADD_ONS.filter((a) => selectedTags.includes(a.id)),
    [selectedTags]
  );

  const totalCents =
    CORE_PRICE_CENTS + selectedAddons.reduce((s, a) => s + a.priceCents, 0);

  useEffect(() => {
    requestAnimationFrame(() => setShow(true));
    const confettiTimeout = setTimeout(fireConfetti, 300);

    const timer = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(timer);
          navigate("/book?from=payment");
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => {
      clearInterval(timer);
      clearTimeout(confettiTimeout);
    };
  }, [navigate]);

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <div className="container mx-auto px-4 pt-24 pb-16 max-w-2xl">
        {/* Confirmation */}
        <div
          className={`text-center mb-10 transition-all duration-700 ease-out ${
            show ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
          }`}
        >
          <div className="relative w-24 h-24 mx-auto mb-6">
            <div
              className="absolute inset-0 rounded-full bg-primary/10 animate-ping"
              style={{ animationDuration: "2s" }}
            />
            <div className="relative w-24 h-24 rounded-full bg-primary/10 flex items-center justify-center">
              <CheckCircle size={48} className="text-primary" />
            </div>
          </div>

          <div className="flex items-center justify-center gap-2 mb-3">
            <Sparkles size={20} className="text-primary animate-pulse" />
            <h1 className="font-display text-3xl md:text-4xl text-foreground">
              You're All Set!
            </h1>
            <Sparkles size={20} className="text-primary animate-pulse" />
          </div>
          <p className="text-muted-foreground max-w-md mx-auto text-lg">
            Welcome to Elara Health. Your plan is active — let's schedule your
            first consultation.
          </p>
        </div>

        {/* Plan Summary */}
        <div
          className={`mb-10 transition-all duration-700 ease-out delay-150 ${
            show ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
          }`}
        >
          <Card className="border-primary/30 bg-primary/5">
            <CardContent className="p-6">
              <div className="flex items-center gap-2 mb-4">
                <Package size={18} className="text-primary" />
                <h2 className="font-display text-lg text-foreground">
                  Your Plan
                </h2>
              </div>

              <div className="space-y-3">
                <div className="flex justify-between text-sm">
                  <span className="text-foreground font-medium">
                    Core Membership
                  </span>
                  <span className="text-foreground">
                    {formatPrice(CORE_PRICE_CENTS)}/mo
                  </span>
                </div>

                {selectedAddons.map((addon) => (
                  <div key={addon.id} className="flex justify-between text-sm">
                    <span className="text-muted-foreground flex items-center gap-2">
                      <span
                        className={`w-2 h-2 rounded-full ${addon.color
                          .replace("/10", "")
                          .replace("border-", "")
                          .split(" ")[0]}`}
                      />
                      {addon.name}
                    </span>
                    <span className="text-muted-foreground">
                      +{formatPrice(addon.priceCents)}/mo
                    </span>
                  </div>
                ))}

                {selectedAddons.length === 0 && (
                  <p className="text-xs text-muted-foreground italic">
                    Core membership only — you can add categories anytime from
                    your dashboard.
                  </p>
                )}
              </div>

              <div className="border-t border-primary/20 mt-4 pt-3 flex justify-between items-baseline">
                <span className="font-display text-foreground">
                  Monthly Total
                </span>
                <span className="font-display text-2xl text-foreground">
                  {formatPrice(totalCents)}
                  <span className="text-sm text-muted-foreground font-sans">
                    /mo
                  </span>
                </span>
              </div>

              <Link
                to={`/customize${selectedTags.length > 0 ? `?tags=${selectedTags.join(",")}` : ""}`}
                className="inline-flex items-center gap-1.5 text-xs text-primary hover:underline mt-3"
              >
                <Pencil size={12} /> Modify Plan
              </Link>
            </CardContent>
          </Card>
        </div>

        {/* Next: Book Consultation */}
        <div
          className={`text-center space-y-4 transition-all duration-700 ease-out delay-300 ${
            show ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
          }`}
        >
          <div className="flex items-center justify-center gap-2 text-muted-foreground mb-2">
            <CalendarDays size={18} className="text-primary" />
            <span className="text-sm font-medium">Next up: schedule your consultation</span>
          </div>
          <Button
            onClick={() => navigate("/book?from=payment")}
            className="w-full max-w-xs gap-2"
          >
            Book Consultation <ArrowRight size={16} />
          </Button>
          <p className="text-xs text-muted-foreground">
            Redirecting in {countdown} second{countdown !== 1 ? "s" : ""}…
          </p>
        </div>
      </div>
    </div>
  );
};

export default PaymentSuccess;
